import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AiSolutionSearchOption, NewAiSolutionUsageRequest } from '@/app/interfaces/new-ai-solution-usage';
import { NewAiSolutionUsageService } from '@/app/services/ia-services/new-ai-solution-usage.service';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { getError } from '@/app/utils/form-errors';
import { DesyAngularModule } from 'desy-angular';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AiSolutionSearchComponent } from './components/ai-solution-search/ai-solution-search.component';

interface StoredUserData {
  department: string;
  email: string;
  orgCode: string;
  orgName: string;
  requestingPerson: string;
  roleCode: string;
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' ? value : '';
}

function readDisplayValue(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' || typeof value === 'number' ? value.toString() : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStoredUserData(): StoredUserData {
  const emptyUserData: StoredUserData = {
    department: '',
    email: '',
    orgCode: '',
    orgName: '',
    requestingPerson: '',
    roleCode: '',
  };

  const storedUserData = localStorage.getItem('userData');
  if (!storedUserData) return emptyUserData;

  try {
    const userData: unknown = JSON.parse(storedUserData);
    if (!isRecord(userData)) return emptyUserData;

    const givenName = readString(userData, 'givenName');
    const surname1 = readString(userData, 'surname1');
    const surname2 = readString(userData, 'surname2');
    const name = [givenName, surname1, surname2].filter(Boolean).join(' ');

    return {
      department: readString(userData, 'department'),
      email: readString(userData, 'email'),
      orgCode: readString(userData, 'orgCode'),
      orgName: readString(userData, 'orgName'),
      requestingPerson: [readDisplayValue(userData, 'rpt'), name, readDisplayValue(userData, 'stationWork')]
        .filter(Boolean)
        .join(' - '),
      roleCode: readDisplayValue(userData, 'roleCode'),
    };
  } catch {
    return emptyUserData;
  }
}

@Component({
  selector: 'app-new-ai-solution-usage',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonsComponent,
    DesyAngularModule,
    ConfirmDialog,
    AiSolutionSearchComponent,
  ],
  templateUrl: './new-ai-solution-usage.component.html',
  styleUrl: './new-ai-solution-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class NewAiSolutionUsageComponent {
  private readonly userData = getStoredUserData();
  readonly getError = getError;
  readonly formSubmitted = signal(false);
  readonly selectedAiSolution = signal<AiSolutionSearchOption | null>(null);
  readonly hasAiSolutionSelection = computed(() => this.selectedAiSolution() !== null);
  readonly aiSolutionTypeOptions = [
    { label: 'Herramienta de productividad', value: 1 },
    { label: 'Agente', value: 2 },
  ];

  readonly form = this.formBuilder.group({
    creationDate: this.formBuilder.control<Date | null>(new Date(), Validators.required),
    creationTime: this.formBuilder.control<Date | null>(new Date(), Validators.required),
    solutionUsageName: ['', [Validators.required, Validators.maxLength(120)]],
    department: [{ value: this.userData.department, disabled: true }, Validators.required],
    requestingOrg: [{ value: this.userData.orgName, disabled: true }, Validators.required],
    requestingPerson: [{ value: this.userData.requestingPerson, disabled: true }, Validators.required],
    requestingPersonEmail: [this.userData.email, [Validators.required, Validators.email]],
    solutionName: ['', Validators.required],
    aiSolutionDescription: ['', [Validators.required, Validators.maxLength(500)]],
    expectedUse: ['', [Validators.required, Validators.maxLength(500)]],
    aiSolutionType: this.formBuilder.control<number | null>(null, Validators.required),
  });

  constructor(
    private formBuilder: FormBuilder,
    private location: Location,
    private newAiSolutionUsageService: NewAiSolutionUsageService,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {}

  onAiSolutionSelectionChanged(option: AiSolutionSearchOption | null): void {
    const descriptionControl = this.form.controls.aiSolutionDescription;

    if (!option) {
      this.selectedAiSolution.set(null);
      descriptionControl.enable({ emitEvent: false });
      this.form.patchValue({
        aiSolutionDescription: '',
      });
      return;
    }

    this.selectedAiSolution.set(option);
    descriptionControl.enable({ emitEvent: false });
    this.form.patchValue({
      aiSolutionDescription: option.description,
    });

    if (option.mode === 'existing') {
      descriptionControl.disable({ emitEvent: false });
    }
  }

  async onSave(): Promise<void> {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.hasAiSolutionSelection()) {
      return;
    }

    try {
      await this.newAiSolutionUsageService.createNewAiSolutionUsage(this.buildPayload());
      this.resetFormState();
      this.router.navigate(['/catalogo-ia/uso-de-solucion-ia/listado-uso-de-solucion-ia']);
    } catch {
      return;
    }
  }

  backPage(): void {
    this.location.back();
  }

  confirm(): void {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.hasAiSolutionSelection()) {
      return;
    }

    this.confirmationService.confirm({
      message: '¿Seguro que desea crear un uso de solución IA?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Guardar',
      },
      accept: () => {
        this.onSave();
      },
    });
  }

  private buildPayload(): NewAiSolutionUsageRequest {
    const rawFormData = this.form.getRawValue();

    return {
      creationDate: this.formatDate(rawFormData.creationDate),
      creationTime: this.formatTime(rawFormData.creationTime),
      solutionUsageName: this.trim(rawFormData.solutionUsageName),
      department: this.trim(rawFormData.department),
      requestingOrg: this.trim(this.userData.orgCode),
      requestingPerson: this.trim(this.userData.roleCode),
      requestingPersonEmail: this.trim(rawFormData.requestingPersonEmail),
      solutionName: this.trim(rawFormData.solutionName),
      aiSolutionDescription: this.trim(rawFormData.aiSolutionDescription),
      expectedUse: this.trim(rawFormData.expectedUse),
      aiSolutionType: rawFormData.aiSolutionType ?? 0,
    };
  }

  private resetFormState(): void {
    this.form.controls.aiSolutionDescription.enable({ emitEvent: false });
    this.form.reset({
      creationDate: new Date(),
      creationTime: new Date(),
      solutionUsageName: '',
      department: this.userData.department,
      requestingOrg: this.userData.orgName,
      requestingPerson: this.userData.requestingPerson,
      requestingPersonEmail: this.userData.email,
      solutionName: '',
      aiSolutionDescription: '',
      expectedUse: '',
      aiSolutionType: null,
    });
    this.selectedAiSolution.set(null);
    this.form.get('department')?.disable();
    this.form.get('requestingOrg')?.disable();
    this.formSubmitted.set(false);
  }

  private trim(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private formatDate(value: Date | null): string | null {
    if (!value) return null;

    return value.toISOString();
  }

  private formatTime(value: Date | null): string | null {
    if (!value) return null;

    const hours = this.pad(value.getHours());
    const minutes = this.pad(value.getMinutes());
    return `${hours}:${minutes}`;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
